import {
  BackLink,
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Screen } from "~/components/ui/Screen";
import { UserForm } from "~/components/users/UserForm";
import {
  ApiError,
  deleteAdminUser,
  fetchAdminUser,
  updateAdminUser,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";
import type { AdminUserUpdate } from "~/types/users-admin";

function translateUserError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "userEditor.errors.selfChange";
    if (error.status === 422) return "userEditor.errors.validation";
    return "common.somethingWentWrong";
  }
  return "userEditor.errors.network";
}

/** Safe account editor; authentication identity and commerce data remain read-only. */
export function UserEditorRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = "" } = useParams<{ id: string }>();
  const currentUser = useAuthStore((state) => state.user);
  const isSelf = currentUser?.id === id;

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const user = useQuery({
    queryKey: queryKeys.user(id),
    queryFn: ({ signal }) => fetchAdminUser(id, signal),
    enabled: id.length > 0,
  });

  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.usersRoot });

  const update = useMutation({
    mutationFn: (body: AdminUserUpdate) => updateAdminUser(id, body),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.user(updated.id), updated);
      await Promise.all([
        invalidateLists(),
        // Granting an override by hand consumes any pending invite for the same
        // address, so its audit row may have changed as part of this mutation.
        queryClient.invalidateQueries({
          queryKey: queryKeys.membershipInvites,
        }),
      ]);
      setSavedOpen(true);
    },
    onError: (error) => setErrorKey(translateUserError(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteAdminUser(id),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.user(id) });
      await Promise.all([
        invalidateLists(),
        // Account deletion erases invitation rows addressed to that user so their
        // e-mail is not retained as audit data. Refresh the adjacent history table.
        queryClient.invalidateQueries({
          queryKey: queryKeys.membershipInvites,
        }),
      ]);
      void navigate("/users");
    },
    onError: (error) => {
      setConfirmingDelete(false);
      setErrorKey(translateUserError(error));
    },
  });

  const header = (
    <Box sx={{ marginBottom: 3 }}>
      <BackLink
        to="/users"
        label={t("userEditor.backToList")}
        testId="user.back"
      />
      <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1 }}>
        <WithHalfdot size={11}>{t("userEditor.title")}</WithHalfdot>
      </Typography>
    </Box>
  );

  if (user.isPending) {
    return (
      <Screen maxWidth="xl">
        {header}
        <LoadingState />
      </Screen>
    );
  }

  if (user.isError) {
    const notFound =
      user.error instanceof ApiError &&
      (user.error.status === 404 || user.error.status === 422);
    return (
      <Screen maxWidth="xl">
        {header}
        {notFound ? (
          <EmptyState message={t("userEditor.notFound")} />
        ) : (
          <ErrorState onRetry={() => void user.refetch()} />
        )}
      </Screen>
    );
  }

  const busy = update.isPending || remove.isPending;

  return (
    <Screen maxWidth="xl">
      {header}

      <UserForm
        key={`${user.data.id}:${user.data.updated_at}`}
        initial={user.data}
        isSelf={isSelf}
        busy={busy}
        errorKey={errorKey}
        onSubmit={(body) => {
          setErrorKey(null);
          update.mutate(body);
        }}
        actions={
          <Button
            color="error"
            variant="text"
            disabled={busy || isSelf}
            onClick={() => setConfirmingDelete(true)}
            data-testid="user.delete"
          >
            {t("userEditor.delete")}
          </Button>
        }
      />

      <Snackbar
        open={savedOpen}
        autoHideDuration={3000}
        onClose={() => setSavedOpen(false)}
        message={t("userEditor.saved")}
        data-testid="user.saved"
      />

      <Dialog
        open={confirmingDelete}
        onClose={busy ? undefined : () => setConfirmingDelete(false)}
      >
        <DialogTitle>{t("userEditor.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("userEditor.deleteConfirmBody", { email: user.data.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => remove.mutate()}
            disabled={busy}
            data-testid="user.delete-confirm"
          >
            {t("userEditor.deleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Screen>
  );
}
